import { ChartInsight } from '@api/api/insights';
import {
  ChartAPIReturn,
  EventsConfig,
  INSIGHTS_IN_DOMAIN_CHANGE_CUSTOM_EVENT,
  InsightsInDomain,
  InsightsInDomainDetail,
  InsightsInDomainEvent,
} from '@chart';
import {
  RefObject,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getInsightEntityTag } from './utils';

interface UseInsightsInDomainProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  eventsConfig: EventsConfig;
  chartApiRef: RefObject<ChartAPIReturn | null>;
  insights: ChartInsight[];
  chartWidth?: number;
  chartHeight?: number;
}

export const useInsightsInDomain = ({
  canvasRef,
  eventsConfig,
  chartApiRef,
  insights,
  chartWidth,
  chartHeight,
}: UseInsightsInDomainProps) => {
  const [insightsInDomain, setInsightsInDomain] = useState<InsightsInDomain[]>(
    []
  );
  const [zoomIntoViewText, setZoomIntoViewText] =
    useState<InsightsInDomainDetail['helperText']>(null);

  const deferredChartWidth = useDeferredValue(chartWidth);
  const deferredChartHeight = useDeferredValue(chartHeight);

  const filteredInsights = useMemo(() => {
    if (!eventsConfig.TMI) {
      return [];
    }

    if (!eventsConfig.INSIGHTS) {
      return insights;
    }

    return insights.filter(insight => {
      const entityTag = getInsightEntityTag(insight);
      return eventsConfig.INSIGHTS?.[entityTag];
    });
  }, [insights, eventsConfig]);

  useEffect(() => {
    const canvasElement = canvasRef.current;

    const insightsInDomainListener = (event: Event) => {
      const detail = (event as InsightsInDomainEvent).detail;

      if (detail.insightsInDomain) {
        setInsightsInDomain(detail.insightsInDomain);
      }

      setZoomIntoViewText(detail.helperText);
    };

    if (canvasElement) {
      canvasElement.addEventListener(
        INSIGHTS_IN_DOMAIN_CHANGE_CUSTOM_EVENT,
        insightsInDomainListener
      );
    }

    return () => {
      canvasElement?.removeEventListener(
        INSIGHTS_IN_DOMAIN_CHANGE_CUSTOM_EVENT,
        insightsInDomainListener
      );
    };
  }, [eventsConfig]);

  useEffect(() => {
    const chartApi = chartApiRef.current;
    if (!chartApi) {
      return;
    }

    const base = chartApi.getBaseData();

    if (!base?.panes) {
      return;
    }

    chartApi.displayInsights(filteredInsights);
  }, [eventsConfig, filteredInsights, deferredChartWidth, deferredChartHeight]);

  return {
    insightsInDomain,
    zoomIntoViewText,
  };
};
